import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'

interface RequireRoleProps {
  /** 'staff' covers both admin and employee; 'admin' is the narrower staff
   *  surface — the recycle bin, which is an admin recovery tool and not
   *  something an employee should reach at all; 'client' is the portal. */
  allow: 'staff' | 'client' | 'admin'
  children: ReactNode
}

/**
 * Gates a route branch on auth + role. Unauthenticated visitors go to
 * /login?next=<path>; an authenticated visitor on the wrong surface (a
 * client hitting /crm, or staff hitting /portal) is sent to the surface
 * that matches their role rather than shown a dead end.
 */
export function RequireRole({ allow, children }: RequireRoleProps) {
  const { loading, session, isStaff, isClient, isAdmin } = useAuth()
  const location = useLocation()

  if (loading) {
    return <div className="min-h-screen" />
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  const allowed = allow === 'staff' ? isStaff : allow === 'admin' ? isAdmin : isClient
  if (!allowed) {
    // A signed-in visitor with no role at all (profile row missing or not yet
    // provisioned) used to be redirected to /portal from /portal — an infinite
    // self-redirect that rendered as a blank screen. Send them somewhere real.
    const target = isStaff ? '/crm' : isClient ? '/portal' : null
    if (target && target !== location.pathname) {
      return <Navigate to={target} replace />
    }
    if (!target) {
      return (
        <div className="min-h-screen flex items-center justify-center px-6 bg-background">
          <div className="max-w-md text-center">
            <h1 className="text-lg font-bold text-foreground">Account not ready yet</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your account doesn&apos;t have access to this area yet. Try signing in again, or
              contact us if this keeps happening.
            </p>
            <Link
              to="/login"
              className="inline-flex mt-5 min-h-11 items-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

